import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, downloadAuthedFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "./PaieDashboard";
import { Plus, ArrowLeft, Download, CheckCircle2, XCircle, Send, Loader2, FileSpreadsheet, Building2 } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => `${Number(n||0).toLocaleString("fr-FR")} GNF`;

export function PaieRunsListPage() {
  const [runs, setRuns] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const currentMonth = new Date().toISOString().slice(0, 7);
  const [period, setPeriod] = useState(currentMonth);
  const [lines, setLines] = useState([]);

  const load = async () => {
    try {
      const [{ data: rs }, { data: cs }] = await Promise.all([
        api.get("/payroll/runs"),
        api.get("/payroll/configs"),
      ]);
      setRuns(rs); setConfigs(cs);
    } catch { toast.error("Erreur"); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setLines(configs.map(c => ({
      user_id: c.user_id,
      user_name: c.user?.full_name || "?",
      position: c.position,
      type: c.employment_type,
      hours_worked: c.employment_type === "teacher_hourly" ? 80 : 0,
      overtime_hours: 0,
      bonus_gnf: 0,
      deduction_gnf: 0,
      advance_gnf: 0,
      absence_days: 0,
    })));
    setOpen(true);
  };

  const updateLine = (i, field, val) => {
    const next = [...lines];
    next[i] = { ...next[i], [field]: val };
    setLines(next);
  };

  const createRun = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/payroll/runs", {
        period,
        lines: lines.map(l => ({
          user_id: l.user_id,
          hours_worked: Number(l.hours_worked) || 0,
          overtime_hours: Number(l.overtime_hours) || 0,
          bonus_gnf: Number(l.bonus_gnf) || 0,
          deduction_gnf: Number(l.deduction_gnf) || 0,
          advance_gnf: Number(l.advance_gnf) || 0,
          absence_days: Number(l.absence_days) || 0,
        })),
      });
      toast.success(`Run créé — Total net : ${fmt(data.total_net)}`);
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="paie-runs-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Runs de paie mensuels</h1>
          <p className="text-slate-500 mt-1">{runs.length} run(s) au total.</p>
        </div>
        <Button onClick={openCreate} disabled={configs.length === 0} className="bg-[#166534] hover:bg-[#14532d] text-white" data-testid="new-run">
          <Plus className="h-4 w-4 mr-2" />Nouveau run
        </Button>
      </div>

      {configs.length === 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-sm text-orange-800">
          ⚠️ Aucun employé configuré. Rendez-vous sur <Link to="/dashboard/paie/employes" className="font-bold underline">Configuration employés</Link> avant de créer un run.
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Période</th>
              <th className="px-4 py-3">Employés</th>
              <th className="px-4 py-3 text-right">Brut</th>
              <th className="px-4 py-3 text-right">Net</th>
              <th className="px-4 py-3">Statut</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {runs.map(r => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`run-${r.id}`}>
                <td className="px-4 py-2 font-mono font-bold text-[#1E3A8A]">{r.period}</td>
                <td className="px-4 py-2 tabular-nums">{r.employee_count}</td>
                <td className="px-4 py-2 text-right tabular-nums">{fmt(r.total_gross)}</td>
                <td className="px-4 py-2 text-right tabular-nums font-bold text-[#166534]">{fmt(r.total_net)}</td>
                <td className="px-4 py-2"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-2 text-right"><Link to={`/dashboard/paie/runs/${r.id}`}><Button size="sm" variant="outline">Ouvrir</Button></Link></td>
              </tr>
            ))}
            {runs.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Aucun run.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Create run dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nouveau run de paie</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Période (mois)</Label><Input type="month" value={period} onChange={e => setPeriod(e.target.value)} className="max-w-xs" data-testid="run-period" /></div>
            <div className="border border-slate-200 rounded-lg overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-2 py-2 text-left">Employé</th>
                    <th className="px-2 py-2 text-left">Type</th>
                    <th className="px-2 py-2 text-center">Heures</th>
                    <th className="px-2 py-2 text-center">H. Supp</th>
                    <th className="px-2 py-2 text-center">Absence (j)</th>
                    <th className="px-2 py-2 text-center">Prime</th>
                    <th className="px-2 py-2 text-center">Retenue</th>
                    <th className="px-2 py-2 text-center">Avance</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={l.user_id} className="border-t border-slate-100">
                      <td className="px-2 py-1"><div className="font-semibold">{l.user_name}</div><div className="text-[10px] text-slate-500">{l.position}</div></td>
                      <td className="px-2 py-1"><span className={`text-[10px] px-1 py-0.5 rounded ${l.type === "teacher_hourly" ? "bg-purple-100" : "bg-blue-100"}`}>{l.type === "teacher_hourly" ? "Horaire" : "Mensuel"}</span></td>
                      <td className="px-1"><Input type="number" value={l.hours_worked} onChange={e => updateLine(i, "hours_worked", e.target.value)} className="text-center h-8 w-20" disabled={l.type !== "teacher_hourly"} /></td>
                      <td className="px-1"><Input type="number" value={l.overtime_hours} onChange={e => updateLine(i, "overtime_hours", e.target.value)} className="text-center h-8 w-16" /></td>
                      <td className="px-1"><Input type="number" step="0.5" value={l.absence_days} onChange={e => updateLine(i, "absence_days", e.target.value)} className="text-center h-8 w-16" disabled={l.type === "teacher_hourly"} /></td>
                      <td className="px-1"><Input type="number" value={l.bonus_gnf} onChange={e => updateLine(i, "bonus_gnf", e.target.value)} className="text-center h-8 w-24" /></td>
                      <td className="px-1"><Input type="number" value={l.deduction_gnf} onChange={e => updateLine(i, "deduction_gnf", e.target.value)} className="text-center h-8 w-24" /></td>
                      <td className="px-1"><Input type="number" value={l.advance_gnf} onChange={e => updateLine(i, "advance_gnf", e.target.value)} className="text-center h-8 w-24" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={createRun} disabled={saving || lines.length === 0} className="bg-[#166534] text-white" data-testid="save-run">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Calculer & Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function PaieRunDetailPage() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [decision, setDecision] = useState({ open: false, action: "approve", notes: "" });

  const load = async () => {
    try {
      const { data } = await api.get(`/payroll/runs/${runId}`);
      setRun(data);
    } catch { toast.error("Erreur"); }
  };
  useEffect(() => { load(); }, [runId]);

  const submit = async () => {
    try {
      await api.post(`/payroll/runs/${runId}/submit`);
      toast.success("Run soumis au workflow de validation");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const doDecision = async () => {
    try {
      await api.post(`/payroll/runs/${runId}/decision`, { decision: decision.action, notes: decision.notes });
      toast.success(decision.action === "approve" ? "Approuvé, étape suivante" : "Rejeté");
      setDecision({ open: false, action: "approve", notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (!run) return <div className="p-8 text-slate-400">Chargement...</div>;

  const apiBase = api.defaults.baseURL;
  const canDecide = ["pending_accountant","pending_finance","pending_director","pending_founder"].includes(run.status);

  return (
    <div className="space-y-6" data-testid="run-detail">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link to="/dashboard/paie/runs" className="text-sm text-slate-500 hover:text-[#1E3A8A]"><ArrowLeft className="inline h-4 w-4 mr-1" />Retour</Link>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A] mt-1">Paie {run.period}</h1>
          <div className="flex items-center gap-2 mt-2">
            <StatusBadge status={run.status} />
            <span className="text-sm text-slate-500">{run.employee_count} employé(s)</span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {run.status === "draft" && <Button onClick={submit} className="bg-[#1E3A8A] text-white" data-testid="submit-run"><Send className="h-4 w-4 mr-2" />Soumettre au workflow</Button>}
          {canDecide && (
            <>
              <Button onClick={() => setDecision({ open: true, action: "reject", notes: "" })} className="bg-red-600 hover:bg-red-700 text-white" data-testid="reject-run"><XCircle className="h-4 w-4 mr-1" />Rejeter</Button>
              <Button onClick={() => setDecision({ open: true, action: "approve", notes: "" })} className="bg-[#166534] hover:bg-[#14532d] text-white" data-testid="approve-run"><CheckCircle2 className="h-4 w-4 mr-1" />Approuver cette étape</Button>
            </>
          )}
          <Button variant="outline" onClick={() => downloadAuthedFile(`/payroll/runs/${runId}/export.csv`, `paie-${runId}.csv`).catch(() => toast.error("Export impossible"))} data-testid="export-csv"><FileSpreadsheet className="h-4 w-4 mr-2" />Export CSV</Button>
          {run.status === "paid" && <Button variant="outline" onClick={() => downloadAuthedFile(`/payroll/runs/${runId}/bank-transfer.txt`, `virements-${runId}.txt`).catch(() => toast.error("Export impossible"))} data-testid="bank-file"><Building2 className="h-4 w-4 mr-2" />Fichier virements</Button>}
        </div>
      </div>

      {/* Totaux */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 rounded-2xl p-5"><div className="text-xs uppercase font-bold text-slate-500">Total brut</div><div className="text-2xl font-extrabold text-[#1E3A8A] tabular-nums">{fmt(run.total_gross)}</div></div>
        <div className="bg-orange-50 rounded-2xl p-5"><div className="text-xs uppercase font-bold text-slate-500">Total retenues</div><div className="text-2xl font-extrabold text-[#EA580C] tabular-nums">{fmt(run.total_gross - run.total_net)}</div></div>
        <div className="bg-green-50 rounded-2xl p-5"><div className="text-xs uppercase font-bold text-slate-500">Total net à payer</div><div className="text-2xl font-extrabold text-[#166534] tabular-nums">{fmt(run.total_net)}</div></div>
      </div>

      {/* Détail lignes */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200"><h2 className="font-heading font-bold text-lg text-[#1E3A8A]">Détail des salaires</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2">Employé</th>
                <th className="px-3 py-2">Poste</th>
                <th className="px-3 py-2 text-right">Base</th>
                <th className="px-3 py-2 text-right">H.Supp</th>
                <th className="px-3 py-2 text-right">Primes</th>
                <th className="px-3 py-2 text-right">Brut</th>
                <th className="px-3 py-2 text-right">CNSS</th>
                <th className="px-3 py-2 text-right">Impôt</th>
                <th className="px-3 py-2 text-right">Ret+Av</th>
                <th className="px-3 py-2 text-right font-bold">Net</th>
              </tr>
            </thead>
            <tbody>
              {run.lines.map(l => (
                <tr key={l.user_id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-semibold">{l.user_name}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{l.position}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(l.base_pay)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(l.overtime_pay)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmt(l.fixed_bonus + l.bonus)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmt(l.gross)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-red-600">{fmt(l.cnss)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-red-600">{fmt(l.tax)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-red-600">{fmt(l.deduction + l.advance)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-extrabold text-[#166534]">{fmt(l.net)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Historique workflow */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
        <h2 className="font-heading font-bold text-lg text-[#1E3A8A] mb-3">Historique de validation</h2>
        <div className="space-y-2 text-sm">
          {(run.workflow_history || []).map((h, i) => (
            <div key={i} className="flex items-start gap-3 pb-2 border-b border-slate-100">
              <div className="w-2 h-2 rounded-full bg-[#1E3A8A] mt-1.5"></div>
              <div className="flex-1">
                <div><strong>{h.by_name}</strong> — {h.action}</div>
                <div className="text-xs text-slate-500">{new Date(h.at).toLocaleString("fr-FR")}</div>
                {h.notes && <div className="text-xs text-slate-600 mt-1">{h.notes}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Decision dialog */}
      <Dialog open={decision.open} onOpenChange={(o) => !o && setDecision({...decision, open: false})}>
        <DialogContent>
          <DialogHeader><DialogTitle>{decision.action === "approve" ? "Approuver cette étape" : "Rejeter le run"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Notes (optionnel)</Label>
            <Textarea rows={3} value={decision.notes} onChange={e => setDecision({...decision, notes: e.target.value})} data-testid="decision-notes" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecision({...decision, open: false})}>Annuler</Button>
            <Button onClick={doDecision} className={decision.action === "approve" ? "bg-[#166534] text-white" : "bg-red-600 text-white"} data-testid="confirm-decision">Confirmer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
